import React, { useEffect, useState } from 'react';
import isEmpty from 'lodash-es/isEmpty';

import { MenuItem } from '@material-ui/core';

import { UtilService } from 'app/services/util.service';
import { Dictionary, DictionaryEntry } from 'app/services/dict.service';
import { AnswerGroup } from 'app/shared/interfaces/AnswerGroup.interface';
import { WrappedControlProps } from 'app/components/Controls/WrappedControlProps';
import { FieldsEnum } from 'app/shared/enums';

export function useSelectControl({
  answerGroups,
  defaultValue,
  dependsOn,
  dependsOnFieldValue,
  dictionary,
  disabled,
  input,
  label,
  meta: { touched, valid },
  onClick,
  onValueChange,
  onControlBlur,
  readonly,
  subLabel,
  isLoadingResult,
  placeholder,
}: WrappedControlProps & {
  answerGroups?: Array<AnswerGroup>;
  defaultValue?: string;
  dependsOn?: Array<string>;
  dependsOnFieldValue?: string | number;
  dictionary: Dictionary;
}) {
  const [focused, setFocused] = useState(false);
  const error = touched && !valid;

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const optionValue = event.target.value;
    const isKeyNumberType = typeof dictionary[0]?.key === 'number';
    const value = isKeyNumberType ? Number(optionValue) : optionValue;

    input.onChange(value);

    if (onValueChange) {
      onValueChange(value, true);
    }
  };

  const setDefault = () => {
    input.onChange(defaultValue);
  };

  const isDisabled = () => {
    return (
      disabled ||
      (dependsOn && !UtilService.hasValue(dependsOnFieldValue)) ||
      (dictionary && isEmpty(dictionary))
    );
  };

  const readOnly = readonly || dictionary.length < 2 || isLoadingResult;

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleFocus = () => {
    handleClick();

    if (!isDisabled() && !isLoadingResult) {
      setFocused(true);
    }
  };

  const handleBlur = () => {
    setFocused(false);

    if (onControlBlur) {
      onControlBlur();
    }
  };

  const getHasDependsOnWithoutValue = () => {
    return dependsOn && !UtilService.hasValue(dependsOnFieldValue);
  };

  const getHasDictionaryWithoutData = () => {
    return dictionary && isEmpty(dictionary);
  };

  const renderNativeItem = (entry: DictionaryEntry) => (
    <option key={entry.key.toString()} value={entry.key.toString()}>
      {entry.subLabels
        ? [entry.value, ...entry.subLabels].join(', ')
        : entry.value}
    </option>
  );

  const getItemsWithoutGroups = (dict: Dictionary) => {
    return dict.filter((item) => !item.group);
  };

  const renderGroupedItem = (
    groupName: string,
    callback: Function
  ): Array<JSX.Element | null> => {
    return dictionary.map(
      (entry) => groupName === entry.group && callback(entry)
    );
  };

  const renderItemsGroup = (group: AnswerGroup) => {
    return (
      <optgroup key={group.key} label={group.label}>
        {renderGroupedItem(group.groupName, renderNativeItem)}
      </optgroup>
    );
  };

  const getNonEmptyAnswerGroups = (
    ansGroups: Array<AnswerGroup>,
    dict: Dictionary
  ) => {
    return ansGroups.filter((entry) =>
      dict.find((item) => item.group === entry.groupName)
    );
  };

  const renderGroupedItems = (
    ansGroups: Array<AnswerGroup>,
    dict: Dictionary
  ) => {
    return (
      <>
        <optgroup key={'empty'}>
          <option disabled value={''} style={{ display: 'none' }}>
            {placeholder}
          </option>
          {getItemsWithoutGroups(dict).map(renderNativeItem)}
        </optgroup>
        {getNonEmptyAnswerGroups(ansGroups, dict).map((group) =>
          renderItemsGroup(group)
        )}
      </>
    );
  };

  const renderNativeItems = () => {
    if (answerGroups) {
      return renderGroupedItems(answerGroups, dictionary);
    } else {
      return (
        <>
          <option disabled value={''} style={{ display: 'none' }}>
            {placeholder}
          </option>
          {dictionary.map(renderNativeItem)}
        </>
      );
    }
  };

  const getLabel = () => {
    if (!subLabel) {
      return label;
    }

    return `${label} - ${subLabel}`;
  };

  const renderMenuItem = (entry: DictionaryEntry) => (
    <MenuItem key={entry.key.toString()} value={entry.key.toString()}>
      {entry.value}
    </MenuItem>
  );

  const renderMenuItems = () => {
    if (answerGroups) {
      let menuItems: Array<JSX.Element | null> = [];
      answerGroups.forEach((group, index) => {
        menuItems.push(
          <MenuItem disabled key={index}>
            {group.label}
          </MenuItem>
        );
        menuItems.push(...renderGroupedItem(group.groupName, renderMenuItem));
      });
      return menuItems;
    } else {
      return dictionary.map(renderMenuItem);
    }
  };

  const getDictionaryValue = (key: string) => {
    return dictionary.find((entry) => entry.key === key)?.value;
  };

  useEffect(() => {
    if (dictionary.length === 1) {
      input.onChange(dictionary[0].key);

      if (onValueChange) {
        const shouldPatch =
          input.name !== FieldsEnum.TravelCheckoutContractSigner;
        onValueChange(dictionary[0].key, true, shouldPatch);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dictionary]);

  return {
    error,
    focused,
    getHasDependsOnWithoutValue,
    getHasDictionaryWithoutData,
    getLabel,
    handleBlur,
    handleChange,
    handleFocus,
    isDisabled,
    readOnly,
    renderMenuItems,
    renderNativeItems,
    setDefault,
    valid,
    getDictionaryValue,
  };
}
